/*    
<summary>
  This functional component "AddStaffManagementForm" provides form to add new user or update existing user.
  Developer:Sahil Bharti, Created Date:03-January-2025
</summary>
<returns>Returns JSX</returns>
*/

import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Field, Form, Formik, useFormikContext } from "formik";
import { cloneDeep } from "lodash";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import toast from "react-hot-toast";
import { FormattedMessage } from "react-intl";
import {
  integerNumJpRegex,
  integerRegex,
} from "../../../../constants/constants";
import { ActionType } from "../../../../constants/enums/action-type-enum";
import { ModuleType } from "../../../../constants/enums/module-type-enum";
import userTypeEnum from "../../../../constants/enums/user-type-enum";
import { useStore } from "../../../../contexts/store-provider";
import { isOperationPermittedRoleBase } from "../../../../helpers/action-permission-helper";
import { getUserTypeFromLocalStorage } from "../../../../helpers/localstorage-helper";
import { toHalfWidth } from "../../../../helpers/validate-helper";
import { IOption } from "../../../../models/ICommon";
import LoaderButton from "../../../../shared-components/button/loader-button";
import FormikFormImagePicker from "../../../../shared-components/FormikFormHandlers/FormikFormImagePicker";
import FormikFormInput from "../../../../shared-components/FormikFormHandlers/FormikFormInput";
import FormikFormInputAddress from "../../../../shared-components/FormikFormHandlers/FormikFormInputAddress";
import FormikFormInputForRegexCheck from "../../../../shared-components/FormikFormHandlers/FormikFormInputForRegexCheck";
import FormikFormInputMobileNumber from "../../../../shared-components/FormikFormHandlers/FormikFormInputMobileNumber";
import FormikFormInputRegexCheckFormattedNumber from "../../../../shared-components/FormikFormHandlers/FormikFormInputRegexCheckFormattedNumber";
import FormikFormSelect from "../../../../shared-components/FormikFormHandlers/FormikFormSelect";
import FormikFormTextArea from "../../../../shared-components/FormikFormHandlers/FormikFormTextArea";
import {
  AddStaffValidationSchema,
  EditStaffValidationSchema,
} from "../../../../shared-components/FormikFormHandlers/FormikValidationSchema";
import FormLoader from "../../../../shared-components/FormLoader/FormLoader";
import { ShowHidePasswordButton } from "../../../../shared-components/Grid/custom-grid-formatter";
import Label from "../../../../shared-components/label/label";
import ConfirmEmailPopupHOC from "../../../../shared-components/popup/confirm/confirm-email-popup-hoc";
import ConfirmPopup from "../../../../shared-components/popup/confirm/confirm-popup";
import ConfirmPopupHOC from "../../../../shared-components/popup/confirm/confirm-popup-hoc";
import { formatMessage } from "../../../../translations/format-message";
import "./add-edit-staff-form.css";
import FormikFormPasswordInput from "../../../../shared-components/FormikFormHandlers/FormikFormPasswordInput";
import { FormObserver } from "../../../../shared-components/address/form-observer-staff";

interface IProps {
  id: number;
  submitHandler: (data: any) => void;
  deleteHandler: (data: any) => void;
  onModalClosed: () => void;
  getStaffManagementLoading: boolean;
  addUpdateStaffManagementLoading: boolean;
  initialValues: any;
  deleteStaffManagementLoading: boolean;
  storeOptions: IOption[];
  viewOnly: boolean;
  showConfirmPopup: boolean;
  confirmPopupToggleHandler: () => void;
  showConfirmEmailPopup: boolean;
  confirmEmailPopupToggleHandler: () => void;
}

const AddStaffManagementForm = (props: IProps) => {
  const { viewOnly } = props;
  const { storeManagementStore, staffManagementStore } = useStore();
  const { GetPrefectureUsingPostalCode, prefectureByZipCode } =
    storeManagementStore;
  const {
    GetStaffManagementByEmailService,
    staffEmailState,
    setStaffEmailData,
    resetStaffEmailDetail,
    isEmailCheckedOrFetched,
    getEmailDuplicacyConfirmedData,
    checkIfStaffAlreadyExistInStoreState,
    staffEmailCheckState,
    resetIsEmailCheckedOrFetched,
  } = staffManagementStore;
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [postCode, setPostCode] = useState<any>(0);
  const handleImageChange = (files: File[]) => {
    const previews = files.map((file) => URL.createObjectURL(file));
    setPreviewImages((prevImages) => [...prevImages, ...previews]);
  };
  const onSubmitEmailConfirmation = () => {
    setStaffEmailData();
    resetStaffEmailDetail();
    props.confirmPopupToggleHandler();
  };

  const onSubmitAddAfterEmailConfirmation = () => {
    props.submitHandler(getEmailDuplicacyConfirmedData);
    props.confirmEmailPopupToggleHandler();
  };

  const handleEmailLookup = useCallback(
    async (values: any, setFieldValue: any) => {
      const email = values.Email;
      const storeId = values.StoreId;
      let isValidEmail =
        email &&
        email.match(/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$/);
      if (!storeId || storeId <= 0) {
        toast.error(formatMessage("please_select_store"));
        return;
      }
      if (!email || !(isValidEmail ?? false)) {
        toast.error(formatMessage("enter_valid_email"));
        return;
      }
      await GetStaffManagementByEmailService(email, storeId);
    },
    [GetStaffManagementByEmailService]
  );

  useEffect(() => {
    if (postCode && postCode?.length == 7) {
      GetPrefectureUsingPostalCode(postCode);
    }
  }, [postCode]);

  useEffect(() => {
    if (staffEmailState.success) {
      props.confirmPopupToggleHandler();
    }
  }, [staffEmailState.success]);

  useEffect(() => {
    if (staffEmailCheckState.success) {
      props.confirmEmailPopupToggleHandler();
    }
  }, [staffEmailCheckState.success]);

  return (
    <>
      {props.showConfirmPopup && (
        <ConfirmPopup
          modalSubmit={onSubmitEmailConfirmation}
          modalClosed={() => {
            resetStaffEmailDetail();
            props.confirmPopupToggleHandler();
          }}
          title={formatMessage("users_email_key")}
          message={formatMessage("this_staff_member_is_already_registered")}
          isLoading={false}
        />
      )}
      {props.showConfirmEmailPopup && (
        <ConfirmPopup
          modalSubmit={onSubmitAddAfterEmailConfirmation}
          modalClosed={() => {
            resetIsEmailCheckedOrFetched();
            props.confirmEmailPopupToggleHandler();
          }}
          title={formatMessage("users_email_key")}
          message={formatMessage("this_staff_member_is_already_registered")}
          isLoading={false}
        />
      )}
      <Modal show={true} centered>
        <Modal.Header>
          <Modal.Title>
            {props.id === -1 ? (
              <FormattedMessage id="staff_registration" />
            ) : (
              <FormattedMessage id="staff_details" />
            )}
            <button
              onClick={props.onModalClosed}
              disabled={props.addUpdateStaffManagementLoading}
              className="Crossicons"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="23"
                height="23"
                fill="currentColor"
                className="bi bi-x"
                viewBox="0 0 16 16"
              >
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
              </svg>
            </button>
          </Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={props.initialValues}
          enableReinitialize={true}
          validateOnChange={true}
          validationSchema={
            props.id > 0
              ? EditStaffValidationSchema(props.initialValues?.TotalCount)
              : AddStaffValidationSchema(props.initialValues?.IsOtherStaffEmail)
          }
          validateOnBlur={true}
          onSubmit={async (values) => {
            let tempData = cloneDeep(values);
            tempData.PrefectureName = values.PrefectureName;
            tempData.PostCode = toHalfWidth(values.PostCode);
            tempData.Address = values.Address?.replace(
              values.PrefectureName,
              ""
            );
            tempData.Email = values.Email?.toLowerCase();
            tempData.OrderId = values.OrderId
              ? toHalfWidth(values.OrderId)
              : null;
            tempData.NominationFee = toHalfWidth(values.NominationFee);
            tempData.TelephoneNumber = toHalfWidth(values.TelephoneNumber);
            tempData.NumberOfAvailability = toHalfWidth(
              values.NumberOfAvailability
            );
            props.submitHandler(tempData);
          }}
        >
          {({ values, setFieldValue }) => {
            return (
              <Form className="user-form">
                {props.getStaffManagementLoading ? (
                  <FormLoader loading={props.getStaffManagementLoading} />
                ) : null}
                <FormObserver
                  prefectureByZipCode={prefectureByZipCode}
                  initialPostalCode={props.initialValues.PostCode}
                />
                {/* {JSON.stringify(values)} */}
                <Modal.Body>
                  {/* Row 1 */}
                  <div className="row">
                    <div className="col-sm-6">
                      <div className="form-group mb-3">
                        <Label
                          className="label-style"
                          required={true}
                          label="store_name"
                        />
                        <Field
                          className="form-control"
                          name="StoreId"
                          //   type="text"
                          //   placeholder="enter_store_name"
                          options={props.storeOptions}
                          as={FormikFormSelect}
                          disabled={
                            viewOnly ||
                            (getUserTypeFromLocalStorage() ===
                              userTypeEnum[userTypeEnum.HeadQuarter] &&
                              props.id > 0)
                          }
                        />
                      </div>
                      <div className="form-group mb-3">
                        <Label
                          className="label-style"
                          required={true}
                          label="staff_name"
                        />
                        <Field
                          className="form-control"
                          name="StaffName"
                          type="text"
                          placeholder="enter_staff_name"
                          as={FormikFormInput}
                          disabled={viewOnly}
                        />
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="form-group upload-container">
                        <Label
                          className="label-style p-0"
                          required={false}
                          label="upload_image"
                        />

                        <Field
                          className="form-control"
                          name="Image"
                          type="text"
                          as={FormikFormImagePicker}
                          disabled={viewOnly}
                        />
                      </div>
                      {/* <div className="upload-container">
                        <Label
                          className="label-style"
                          required={true}
                          label="upload_image"
                        />
                        <input
                          id="imageUpload1"
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(event) => {
                            if (event.currentTarget.files) {
                              const fileArray = Array.from(
                                event.currentTarget.files
                              );
                              setFieldValue("Images1", fileArray);
                              handleImageChange(fileArray);
                            }
                          }}
                          className="form-control d-none"
                        />
                        <div
                          className="circle-upload"
                          onClick={() =>
                            document.getElementById("imageUpload1")?.click()
                          }
                        >
                          <span className="upload-icon">+</span>
                        </div>
                      </div> */}
                    </div>
                    <div className="col-sm-6 mb-3">
                      <Label
                        className="label-style"
                        required={true}
                        label="email"
                      />
                      <div className="input-group email-confirmation">
                        <Field
                          className="form-control"
                          validcharacter={["@", "#", "$", "-", "_", "."]}
                          name="Email"
                          // type="email"
                          autoComplete="new-email"
                          placeholder="enter_email"
                          as={FormikFormInput}
                          disabled={viewOnly}
                          isEmailChecked={props.id > 0 ? +false : +true}
                          checkEmailButtonComponent={
                            <button
                              className={`btn ${"btn-light"}`}
                              onClick={() => {
                                handleEmailLookup(values, setFieldValue);
                              }}
                              title={formatMessage("find_email")}
                              type="button"
                              disabled={
                                props.addUpdateStaffManagementLoading ||
                                staffEmailState.inProgress ||
                                viewOnly
                              }
                            >
                              <FontAwesomeIcon icon={faEnvelope} />
                            </button>
                          }
                        />
                      </div>
                      {!(props.id > 0) && (
                        <h6 className="text-muted fw-normal mt-1">
                          <FormattedMessage id="if_staff_is_already_registered" />
                        </h6>
                      )}
                    </div>

                    <div className="col-sm-6 mb-3">
                      <Label
                        className="label-style"
                        required={true}
                        label="contact_number"
                      />
                      <Field
                        className="form-control"
                        name="TelephoneNumber"
                        type="text"
                        placeholder="enter_mobile_number"
                        regex={integerRegex}
                        as={FormikFormInputMobileNumber}
                        disabled={viewOnly}
                      />
                    </div>

                    {props.id < 0 && (
                      <>
                        <div className="col-sm-6 mb-3">
                          <Label
                            className="label-style"
                            required={true}
                            label="new_password"
                          />
                          <div className="position-relative">
                            <Field
                              className="form-control "
                              name="Password"
                              autoComplete="new-password"
                              placeholder="enter_new_password"
                              invalidcharacter={[" "]}
                              validcharacter={[
                                "~",
                                "`",
                                "!",
                                "@",
                                "#",
                                "$",
                                "%",
                                "^",
                                "&",
                                "*",
                                "(",
                                ")",
                                "_",
                                "-",
                                "+",
                                "=",
                                "{",
                                "[",
                                "}",
                                "]",
                                "|",
                                "\\",
                                ":",
                                ";",
                                "'",
                                '"',
                                "<",
                                ",",
                                ">",
                                ".",
                                "?",
                                "/",
                              ]}
                              as={FormikFormPasswordInput}
                              disabled={values.IsOtherStaffEmail}
                            />
                          </div>
                        </div>
                        <div className="col-sm-6 mb-3">
                          <Label
                            className="label-style"
                            required={true}
                            label="confirm_password"
                          />
                          <div className="position-relative">
                            <Field
                              className="form-control"
                              name="ConfirmPassword"
                              autoComplete="new-confirm-password"
                              placeholder="enter_confirm_password"
                              as={FormikFormPasswordInput}
                              invalidcharacter={[" "]}
                              disabled={values.IsOtherStaffEmail}
                              validcharacter={[
                                "~",
                                "`",
                                "!",
                                "@",
                                "#",
                                "$",
                                "%",
                                "^",
                                "&",
                                "*",
                                "(",
                                ")",
                                "_",
                                "-",
                                "+",
                                "=",
                                "{",
                                "[",
                                "}",
                                "]",
                                "|",
                                "\\",
                                ":",
                                ";",
                                "'",
                                '"',
                                "<",
                                ",",
                                ">",
                                ".",
                                "?",
                                "/",
                              ]}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    <div className="col-sm-6 postal-code-width mb-3">
                      <Label className="label-style" label="postal_code" />
                      <Field
                        className="form-control"
                        name="PostCode"
                        type="text"
                        placeholder="enter_post_code"
                        regex={integerNumJpRegex}
                        setState={setPostCode}
                        as={FormikFormInputForRegexCheck}
                        disabled={viewOnly}
                      />
                    </div>
                    <div className="col-sm-6 next-postal-code-width mb-3">
                      <Label className="label-style" label="address" />
                      <Field
                        className="form-control"
                        name="Address"
                        type="text"
                        placeholder="enter_address"
                        prefectureName={values.PrefectureName}
                        validcharacter={["@", "#", "$", "-", "_", ".", "/"]}
                        as={FormikFormInputAddress}
                        disabled={viewOnly}
                      />
                    </div>

                    <div className="col-sm-4 mb-3">
                      <Label
                        required={true}
                        className="label-style"
                        label="no_of_availability"
                      />
                      <Field
                        className="form-control"
                        name="NumberOfAvailability"
                        type="text"
                        placeholder="enter_availability"
                        regex={integerRegex}
                        as={FormikFormInputForRegexCheck}
                        disabled={viewOnly}
                      />
                    </div>
                    <div className="col-sm-4 mb-3">
                      <Label className="label-style" label="nomination_fee" />
                      <Field
                        className="form-control"
                        name="NominationFee"
                        type="text"
                        placeholder="enter_nomination_fee"
                        regex={integerRegex}
                        as={FormikFormInputRegexCheckFormattedNumber}
                        disabled={
                          viewOnly
                            ? viewOnly
                            : values.StoreId > 0
                            ? props.storeOptions?.find(
                                (value) => value.id == values.StoreId
                              )?.data.IsNominationFee == false
                              ? true
                              : false
                            : false
                        }
                      />
                    </div>
                    <div className="col-sm-4 mb-3">
                      <Label className="label-style" label="expertise" />
                      <Field
                        className="form-control"
                        name="Expertise"
                        type="text"
                        placeholder="enter_expertise"
                        as={FormikFormInput}
                        disabled={viewOnly}
                      />
                    </div>
                    <div className="col-sm-4 mb-3">
                      <Label className="label-style" label="gender" />
                      <div className="row">
                        <div className="col-sm-6">
                          <Field
                            className="form-check-input"
                            name="Gender"
                            type="radio"
                            value="0"
                            id="male"
                            disabled={viewOnly}
                          />
                          <label className="form-check-label" htmlFor="male">
                            <FormattedMessage id="male" />
                          </label>
                        </div>
                        <div className="col-sm-6">
                          <Field
                            className="form-check-input"
                            name="Gender"
                            type="radio"
                            value="1"
                            id="female"
                            disabled={viewOnly}
                          />
                          <label className="form-check-label" htmlFor="female">
                            <FormattedMessage id="female" />
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-4 mb-3">
                      <Label className="label-style" label="status" />
                      <div className="row">
                        <div className="col-sm-6">
                          <Field
                            className="form-check-input"
                            name="AccountStatus"
                            type="radio"
                            value="Valid"
                            id="valid"
                            disabled={viewOnly}
                          />
                          <label className="form-check-label" htmlFor="valid">
                            <FormattedMessage id="valid" />
                          </label>
                        </div>
                        <div className="col-sm-6">
                          <Field
                            className="form-check-input"
                            name="AccountStatus"
                            type="radio"
                            value="Invalid"
                            id="invalid"
                            disabled={viewOnly}
                          />
                          <label className="form-check-label" htmlFor="invalid">
                            <FormattedMessage id="invalid" />
                          </label>
                        </div>
                      </div>
                    </div>

                    {props.id > 0 && (
                      <div className="col-sm-6 mb-3">
                        <Label
                          className="label-style"
                          required={true}
                          label="serial_number"
                        />
                        <Field
                          className="form-control"
                          name="OrderId"
                          type="text"
                          placeholder="enter_serial_number"
                          regex={integerRegex}
                          as={FormikFormInputForRegexCheck}
                          disabled={viewOnly}
                        />
                      </div>
                    )}
                    <div className="col-sm-12 mb-3">
                      <Label className="label-style" label="introduction" />
                      <Field
                        className="form-control"
                        name="Introduction"
                        type="text"
                        placeholder="enter_introduction"
                        as={FormikFormTextArea}
                        disabled={viewOnly}
                      />
                      <p className="small text-muted text-end mt-1">{`${values.Introduction.length}/300`}</p>
                    </div>
                  </div>
                </Modal.Body>
                {/* Footer Buttons */}
                <div className="modal-footer center-content">
                  <Button
                    variant="outline-danger"
                    type="button"
                    onClick={props.onModalClosed}
                    disabled={props.addUpdateStaffManagementLoading}
                  >
                    <i className="bi bi-x-lg me-1"></i>
                    <FormattedMessage id="close" />
                  </Button>
                  {isOperationPermittedRoleBase(
                    ModuleType.StaffManagement,
                    ActionType.Edit
                  ) && (
                    <LoaderButton
                      type="submit"
                      iconName="bi-check-lg"
                      isLoading={
                        props.addUpdateStaffManagementLoading ||
                        checkIfStaffAlreadyExistInStoreState.inProgress
                      }
                      text={props.id > -1 ? "button_update" : "button_register"}
                    />
                  )}
                  {props.id > -1 &&
                    isOperationPermittedRoleBase(
                      ModuleType.StaffManagement,
                      ActionType.Delete
                    ) && (
                      <Button
                        variant="danger"
                        type="button"
                        onClick={props.deleteHandler}
                        disabled={
                          props.deleteStaffManagementLoading ||
                          checkIfStaffAlreadyExistInStoreState.inProgress
                        }
                      >
                        <i className="bi bi-trash3-fill me-1"></i>
                        <FormattedMessage id="button_delete" />
                      </Button>
                    )}
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
};

export default ConfirmEmailPopupHOC(
  ConfirmPopupHOC(observer(AddStaffManagementForm))
);
